
import { useState, useEffect, useRef, useCallback } from 'react';

export default function ScrollProgressCar() {
  const [progress, setProgress] = useState(0);
  const [isSticky, setIsSticky] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [hasTriggeredConfetti, setHasTriggeredConfetti] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>(0);
  const confettiParticles = useRef<
    Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      color: string;
      width: number;
      height: number;
      rotation: number;
      rotationSpeed: number;
      opacity: number;
      wobble: number;
      wobbleSpeed: number;
    }>
  >([]);

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const scrollPercent = Math.min(scrollTop / docHeight, 1);
    setProgress(scrollPercent);

    if (placeholderRef.current) {
      const placeholderRect = placeholderRef.current.getBoundingClientRect();
      const trackHeight = 90;
      if (placeholderRect.top <= window.innerHeight - trackHeight) {
        setIsSticky(false);
      } else {
        setIsSticky(true);
      }
    }

    if (scrollPercent > 0.95 && !hasTriggeredConfetti) {
      setShowConfetti(true);
      setHasTriggeredConfetti(true);
    }

    if (scrollPercent < 0.5 && hasTriggeredConfetti) {
      setHasTriggeredConfetti(false);
      setShowConfetti(false);
    }
  }, [hasTriggeredConfetti]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [handleScroll]);

  // Confetti animation – black & white rectangular ticker‑tape pieces
  useEffect(() => {
    if (!showConfetti || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setShowConfetti(false);
      return;
    }

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ['#000000', '#ffffff', '#1a1a1a', '#e5e5e5', '#333333', '#f5f5f5'];

    confettiParticles.current = [];
    for (let i = 0; i < 150; i++) {
      // Spread confetti across the top of the screen, biased toward the right
      const spawnX = canvas.width * 0.3 + Math.random() * canvas.width * 0.65;
      confettiParticles.current.push({
        x: spawnX,
        y: -Math.random() * canvas.height * 0.3 - 20,
        vx: (Math.random() - 0.5) * 3,
        vy: Math.random() * 2.5 + 1.5,
        color: colors[Math.floor(Math.random() * colors.length)],
        width: Math.random() * 12 + 8,
        height: Math.random() * 6 + 3,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 8,
        opacity: 1,
        wobble: Math.random() * Math.PI * 2,
        wobbleSpeed: Math.random() * 0.08 + 0.03,
      });
    }

    let startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let activeParticles = 0;

      confettiParticles.current.forEach((p) => {
        // Gentle wobble side‑to‑side like ticker tape
        p.wobble += p.wobbleSpeed;
        p.x += p.vx + Math.sin(p.wobble) * 1.2;
        p.vy += 0.04; // slow gravity
        p.y += p.vy;
        p.rotation += p.rotationSpeed;

        // Start fading after 2.5 seconds
        if (elapsed > 2500) {
          p.opacity -= 0.008;
        }

        if (p.opacity <= 0 || p.y > canvas.height + 50) return;
        activeParticles++;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = Math.max(0, p.opacity);

        // Draw rectangular ticker‑tape piece
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);

        // Subtle border for white pieces so they're visible
        if (p.color === '#ffffff' || p.color === '#f5f5f5' || p.color === '#e5e5e5') {
          ctx.strokeStyle = 'rgba(0,0,0,0.15)';
          ctx.lineWidth = 0.5;
          ctx.strokeRect(-p.width / 2, -p.height / 2, p.width, p.height);
        }

        ctx.restore();
      });

      if (activeParticles > 0 && elapsed < 6000) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setShowConfetti(false);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [showConfetti]);

  const trackLeftPad = 40;
  const trackRightPad = 120;
  const carWidth = 140;
  const trackWidth =
    trackRef.current?.offsetWidth ??
    (typeof window !== 'undefined' ? window.innerWidth : 1200);
  const maxTravel = trackWidth - trackLeftPad - trackRightPad - carWidth;
  const carLeft = trackLeftPad + progress * maxTravel;

  const flagOpacity = progress > 0.7 ? Math.min(1, (progress - 0.7) / 0.15) : 0.35;
  const carOpacity = progress > 0.96 ? Math.max(0, 1 - (progress - 0.96) / 0.04) : 1;

  // Generate checkered flag pattern with bigger squares
  const checkerSize = 12;
  const flagCols = 4;
  const flagRows = 5;
  const flagWidth = flagCols * checkerSize;
  const flagHeight = flagRows * checkerSize;

  const trackContent = (
    <div
      ref={trackRef}
      className="w-full"
      style={{ height: '90px', background: 'transparent' }}
    >
      {/* Track background */}
      <div className="absolute bottom-0 left-0 right-0 h-[90px]">
        {/* Asphalt surface */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[70px]"
          style={{
            background:
              'linear-gradient(to top, #1a1a1a 0%, #2a2a2a 60%, #1a1a1a 80%, transparent 100%)',
          }}
        >
          {/* Top edge line */}
          <div
            className="absolute top-[4px] left-[30px] right-[30px] h-[2px]"
            style={{
              background:
                'linear-gradient(to right, transparent, rgba(255,255,255,0.3) 5%, rgba(255,255,255,0.3) 95%, transparent)',
            }}
          />
          {/* Bottom edge line */}
          <div
            className="absolute bottom-[4px] left-[30px] right-[30px] h-[2px]"
            style={{
              background:
                'linear-gradient(to right, transparent, rgba(255,255,255,0.3) 5%, rgba(255,255,255,0.3) 95%, transparent)',
            }}
          />
          {/* Center white dashed line */}
          <div className="absolute bottom-[33px] left-[30px] right-[100px] h-[4px] flex items-center">
            {Array.from({ length: 60 }).map((_, i) => (
              <div
                key={i}
                className="flex-1 mx-[2px] h-[2px] rounded-full"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                }}
              />
            ))}
          </div>
          {/* Start line */}
          <div className="absolute top-[6px] bottom-[6px] left-[35px] w-[3px] bg-white/40 rounded-full" />
          {/* Tire marks */}
          {progress > 0.05 && (
            <div
              className="absolute bottom-[20px] h-[1px] rounded-full"
              style={{
                left: `${trackLeftPad + 10}px`,
                width: `${Math.min(progress * maxTravel, maxTravel)}px`,
                background:
                  'linear-gradient(to right, rgba(80,80,80,0.4), rgba(60,60,60,0.2), transparent)',
              }}
            />
          )}
        </div>

        {/* Fade edges */}
        <div
          className="absolute bottom-0 left-0 w-[30px] h-[70px] z-10"
          style={{ background: 'linear-gradient(to right, #0a0a0a, transparent)' }}
        />
        <div
          className="absolute bottom-0 right-0 w-[30px] h-[70px] z-10"
          style={{ background: 'linear-gradient(to left, #0a0a0a, transparent)' }}
        />
      </div>

      {/* F1 Car */}
      <div
        className="absolute z-20"
        style={{
          left: `${carLeft}px`,
          bottom: '12px',
          width: `${carWidth}px`,
          height: '48px',
          filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.6))',
          opacity: carOpacity,
          transition: 'opacity 0.2s ease-out',
        }}
      >
        <img
          src="https://static.readdy.ai/image/342f903f198462f00a1de0518e388745/d8202d2614d697b32c3b04c67c9cfd66.png"
          alt="iBen Consulting F1 Car"
          className="w-full h-full object-contain"
          style={{
            transform: progress > 0.9 ? 'scale(1.05)' : 'scale(1)',
            transition: 'transform 0.3s ease-out',
          }}
        />
      </div>

      {/* Finish flag & pole — bigger checkered squares */}
      <div
        className="absolute bottom-[6px] right-[45px] flex items-end z-20 transition-all duration-500"
        style={{
          opacity: flagOpacity,
          transform: progress > 0.9 ? 'scale(1.1)' : 'scale(1)',
        }}
      >
        <div className="relative flex items-end">
          {/* Pole */}
          <div className="w-[3px] h-[72px] bg-gradient-to-b from-gray-200 to-gray-500 rounded-full shadow-lg" />
          {/* Flag with big checker squares using SVG pattern */}
          <div
            className="absolute left-[3px] overflow-hidden shadow-xl"
            style={{
              top: '-2px',
              width: `${flagWidth}px`,
              height: `${flagHeight}px`,
              animation:
                progress > 0.85 ? 'flagWave 0.4s ease-in-out infinite alternate' : 'none',
              transformOrigin: 'left center',
            }}
          >
            <div className="w-full h-full relative" style={{ background: '#ffffff' }}>
              {Array.from({ length: flagRows }).map((_, row) =>
                Array.from({ length: flagCols }).map((_, col) => {
                  const isBlack = (row + col) % 2 === 0;
                  return (
                    <div
                      key={`${row}-${col}`}
                      className="absolute"
                      style={{
                        left: `${col * checkerSize}px`,
                        top: `${row * checkerSize}px`,
                        width: `${checkerSize}px`,
                        height: `${checkerSize}px`,
                        backgroundColor: isBlack ? '#000000' : '#ffffff',
                      }}
                    />
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div ref={placeholderRef} style={{ height: '90px', position: 'relative' }}>
        {!isSticky && (
          <div className="relative w-full" style={{ height: '90px' }}>
            {trackContent}
          </div>
        )}
      </div>

      {isSticky && (
        <div className="fixed bottom-0 left-0 right-0 z-40" style={{ height: '90px' }}>
          <div className="relative w-full" style={{ height: '90px' }}>
            {trackContent}
          </div>
        </div>
      )}

      {/* Confetti canvas */}
      {showConfetti && (
        <canvas
          ref={canvasRef}
          className="fixed inset-0 z-50 pointer-events-none"
          style={{ width: '100vw', height: '100vh' }}
        />
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes flagWave {
              0% { transform: rotateY(-5deg) scaleX(1); }
              100% { transform: rotateY(5deg) scaleX(0.92); }
            }
          `,
        }}
      />
    </>
  );
}
